import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ToucheReact } from "react";
import type { FamilleSujets, Rendezvous } from "@hoa/protocol";
import type { Planning } from "./ecrans2.js";
import { MINUTE, aLHeure, debutJour, fenetresRdv, instantsDuJour, jourPossible, plusProche, possible, rdvParDefaut, tousLesInstants, type Fenetre } from "./rdv.js";
import type { SkinId } from "./skins.js";
import { Barre, Bulle, Ecran, Etape, Feuille, FeuilleReglable, Retour, heure } from "./ui.js";

/**
 * Ouvrir une tribu (refonte du 1er octobre).
 *
 * Un écran qui tient sans défiler : quatre lignes, Sujet, Où, Quand,
 * Combien. Les trois premières s'ouvrent dans une feuille par le bas :
 *   - le sujet : recherche et familles, uniquement les sujets du catalogue
 *     (Hoa a écrit ses cinq questions pour chacun d'eux) ;
 *   - le lieu : uniquement les adresses du quartier, avec leur repère ;
 *   - le jour et l'heure : un calendrier, puis une roue (heures, minutes
 *     par cinq). Les jours et heures impossibles sont grisés.
 *
 * Ce qui est possible, le serveur le vérifie de toute façon (index.ts,
 * `POST /api/tribus`) : au moins la fenêtre du chat plus cinq minutes
 * d'avance, au plus sept jours, et pendant une session de Hoa quand la
 * régie en a réglé.
 */

const TAILLES: Array<[number, number]> = [
  [3, 8],
  [3, 5],
  [4, 6],
];

/** « Aujourd'hui, 21 h 30 », « Demain, 9 h », « sam. 4 oct., 21 h 30 ». */
export function libelleQuand(t: number, maintenant: number): string {
  const ecart = Math.round((debutJour(t) - debutJour(maintenant)) / 86_400_000);
  const j =
    ecart <= 0
      ? "Aujourd'hui"
      : ecart === 1
        ? "Demain"
        : new Date(t).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  return `${j}, ${heure(t)}`;
}

/* ------------------------------------------------------------------ */
/* L'écran                                                             */
/* ------------------------------------------------------------------ */

const sansAccent = (t: string): string =>
  t
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

type Ouverte = "sujet" | "lieu" | "quand" | null;

export function Creer(props: {
  skin: SkinId;
  secteurId: string;
  maintenant: number;
  /** Les sessions de Hoa : hors session, on crée pour la prochaine. */
  planning: Planning | null;
  envoi: boolean;
  erreur: string | null;
  onRetour: () => void;
  onCreer: (c: { sujetId: string; lieu: number; rdvAt: number; min: number; max: number }) => void;
}): JSX.Element {
  const [familles, setFamilles] = useState<FamilleSujets[]>([]);
  const [lieux, setLieux] = useState<Rendezvous[]>([]);
  const [sujetId, setSujetId] = useState<string | null>(null);
  const [lieu, setLieu] = useState(0);
  const [rdv, setRdv] = useState<number | null>(null);
  const [taille, setTaille] = useState(0);
  const [ouverte, setOuverte] = useState<Ouverte>(null);
  const [chargement, setChargement] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      fetch("/api/sujets").then((r) => r.json() as Promise<{ familles: FamilleSujets[] }>),
      fetch("/api/sectors").then((r) => r.json() as Promise<{ sectors: Array<{ id: string; places: Rendezvous[] }> }>),
    ])
      .then(([c, s]) => {
        setFamilles(c.familles);
        setLieux(s.sectors.find((x) => x.id === props.secteurId)?.places ?? []);
      })
      .catch(() => setChargement("Impossible de charger les sujets. Réessaie dans un instant."));
  }, [props.secteurId]);

  const minute = Math.floor(props.maintenant / MINUTE);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fenetres = useMemo(() => fenetresRdv(props.maintenant, props.planning), [minute, props.planning]);
  // Le rendez-vous choisi reste valable tant qu'il est possible ; sinon le
  // plus proche (le temps passe pendant qu'on hésite).
  const rdvChoisi = useMemo(() => {
    if (rdv !== null && possible(rdv, fenetres)) return rdv;
    if (rdv !== null) return plusProche(rdv, tousLesInstants(fenetres), fenetres);
    return rdvParDefaut(fenetres);
  }, [rdv, fenetres]);

  const choisi = useMemo(() => familles.flatMap((f) => f.sujets).find((s) => s.id === sujetId) ?? null, [familles, sujetId]);
  const place = lieux[lieu] ?? null;
  const pret = choisi !== null && place !== null && rdvChoisi !== null;

  return (
    <Ecran skin={props.skin} label="Nouvelle tribu">
      <Barre>
        <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
          <Retour />
        </button>
        <span className="titre">Nouvelle tribu</span>
      </Barre>
      <div className="hoa-frise hoa-creer">
        <Etape etat="courante" dernier expression="sourire" label="Nouvelle tribu">
          <Bulle>{"De quoi veux-tu parler, et où\u00a0?"}</Bulle>
          {chargement ? <div className="hoa-erreur">{chargement}</div> : null}
          <div className="hoa-reglages-tribu">
            <LigneChoix etiquette="Sujet" valeur={choisi?.label ?? null} vide="Choisir un sujet" onClick={() => setOuverte("sujet")} />
            <LigneChoix
              etiquette="Où"
              valeur={place?.name ?? null}
              sous={place ? [place.landmark, place.address].filter(Boolean).join(" · ") : undefined}
              vide={lieux.length === 0 ? "Aucune adresse" : "Choisir le lieu"}
              desactive={lieux.length === 0}
              onClick={() => setOuverte("lieu")}
            />
            <LigneChoix
              etiquette="Quand"
              valeur={rdvChoisi !== null ? libelleQuand(rdvChoisi, props.maintenant) : null}
              vide="Aucun créneau possible"
              desactive={rdvChoisi === null}
              onClick={() => setOuverte("quand")}
            />
            <div className="hoa-reglage-combien">
              <span className="hoa-etiquette">Combien</span>
              <div className="hoa-choix" role="group" aria-label="Nombre de personnes">
                {TAILLES.map(([a, b], i) => (
                  <button key={i} type="button" aria-pressed={i === taille} onClick={() => setTaille(i)}>
                    {a} à {b}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {props.erreur ? (
            <div className="hoa-erreur" role="alert">
              {props.erreur}
            </div>
          ) : null}
        </Etape>
      </div>
      <button
        type="button"
        className="hoa-go"
        style={{ marginTop: "auto" }}
        disabled={!pret || props.envoi}
        onClick={() => choisi && rdvChoisi !== null && props.onCreer({ sujetId: choisi.id, lieu, rdvAt: rdvChoisi, min: TAILLES[taille]![0], max: TAILLES[taille]![1] })}
      >
        {props.envoi ? "Un instant…" : "Ouvrir la tribu"}
      </button>

      {ouverte === "sujet" ? (
        <FeuilleSujet
          familles={familles}
          choisi={sujetId}
          onChoisir={(id) => {
            setSujetId(id);
            setOuverte(null);
          }}
          onFermer={() => setOuverte(null)}
        />
      ) : null}
      {ouverte === "lieu" ? (
        <Feuille label="Choisir le lieu" onFermer={() => setOuverte(null)}>
          <div className="hoa-liste-feuille" role="group" aria-label="Lieux du quartier">
            {lieux.map((l, i) => (
              <button
                key={`${l.name}-${i}`}
                type="button"
                className="hoa-ligne"
                aria-pressed={i === lieu}
                onClick={() => {
                  setLieu(i);
                  setOuverte(null);
                }}
              >
                <div>
                  <b>{l.name}</b>
                  {l.landmark || l.address ? <span>{[l.landmark, l.address].filter(Boolean).join(" · ")}</span> : null}
                </div>
                {i === lieu ? <Coche /> : null}
              </button>
            ))}
          </div>
        </Feuille>
      ) : null}
      {ouverte === "quand" && rdvChoisi !== null ? (
        <FeuilleQuand
          valeur={rdvChoisi}
          fenetres={fenetres}
          maintenant={props.maintenant}
          onChoisir={(t) => {
            setRdv(t);
            setOuverte(null);
          }}
          onFermer={() => setOuverte(null)}
        />
      ) : null}
    </Ecran>
  );
}

/** Une ligne de réglage : son nom, ce qui est choisi, et un chevron. */
function LigneChoix(props: { etiquette: string; valeur: string | null; vide: string; sous?: string; desactive?: boolean; onClick: () => void }): JSX.Element {
  return (
    <button type="button" className="hoa-ligne-choix" aria-haspopup="dialog" disabled={props.desactive} onClick={props.onClick}>
      <span className="etiquette">{props.etiquette}</span>
      <span className="valeur">
        <b className={props.valeur ? undefined : "vide"}>{props.valeur ?? props.vide}</b>
        {props.sous ? <small>{props.sous}</small> : null}
      </span>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </button>
  );
}

function Coche(): JSX.Element {
  return (
    <svg className="hoa-coche" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Le sujet : chercher, ou parcourir par famille                       */
/* ------------------------------------------------------------------ */

function FeuilleSujet(props: { familles: FamilleSujets[]; choisi: string | null; onChoisir: (id: string) => void; onFermer: () => void }): JSX.Element {
  const familleDuChoix = props.familles.find((f) => f.sujets.some((s) => s.id === props.choisi))?.id;
  const [famille, setFamille] = useState<string | null>(familleDuChoix ?? props.familles[0]?.id ?? null);
  const [q, setQ] = useState("");
  const cherche = sansAccent(q).length >= 2;
  const visibles = useMemo(() => {
    if (cherche) {
      const n = sansAccent(q);
      return props.familles.flatMap((f) => f.sujets.filter((s) => sansAccent(s.label).includes(n)).map((s) => ({ ...s, famille: f.label })));
    }
    return (props.familles.find((f) => f.id === famille)?.sujets ?? []).map((s) => ({ ...s, famille: "" }));
  }, [cherche, q, famille, props.familles]);

  return (
    <FeuilleReglable
      label="Choisir un sujet"
      onFermer={props.onFermer}
      entete={
        <div className="hoa-sujet-entete">
          <input
            className="hoa-champ carre"
            type="search"
            enterKeyHint="search"
            value={q}
            placeholder="Chercher un sujet"
            aria-label="Chercher un sujet"
            autoComplete="off"
            onChange={(e) => setQ(e.target.value)}
          />
          {cherche ? null : (
            <div className="hoa-choix hoa-familles" role="group" aria-label="Familles de sujets">
              {props.familles.map((f) => (
                <button key={f.id} type="button" aria-pressed={f.id === famille} onClick={() => setFamille(f.id)}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
      }
    >
      <div className="hoa-liste-feuille" role="group" aria-label="Sujets">
        {visibles.map((s) => (
          <button key={s.id} type="button" className="hoa-ligne" aria-pressed={s.id === props.choisi} onClick={() => props.onChoisir(s.id)}>
            <div>
              <b>{s.label}</b>
              {s.famille ? <span>{s.famille}</span> : null}
            </div>
            {s.id === props.choisi ? <Coche /> : null}
          </button>
        ))}
        {props.familles.length === 0 ? <p className="hoa-note">Un instant…</p> : null}
        {props.familles.length > 0 && visibles.length === 0 ? <p className="hoa-note">Aucun sujet ne correspond.</p> : null}
      </div>
    </FeuilleReglable>
  );
}

/* ------------------------------------------------------------------ */
/* Le jour et l'heure : un calendrier, puis une roue                   */
/* ------------------------------------------------------------------ */

function FeuilleQuand(props: { valeur: number; fenetres: Fenetre[]; maintenant: number; onChoisir: (t: number) => void; onFermer: () => void }): JSX.Element {
  const [v, setV] = useState(props.valeur);
  const jour = debutJour(v);
  const h = new Date(v).getHours();
  const m = new Date(v).getMinutes();
  const f = props.fenetres;

  const heures = useMemo(
    () =>
      Array.from({ length: 24 }, (_, x) => ({
        v: x,
        texte: String(x).padStart(2, "0"),
        ok: Array.from({ length: 12 }, (_, k) => aLHeure(jour, x, k * 5)).some((t) => possible(t, f)),
      })),
    [jour, f],
  );
  const minutes = useMemo(
    () => Array.from({ length: 12 }, (_, k) => ({ v: k * 5, texte: String(k * 5).padStart(2, "0"), ok: possible(aLHeure(jour, h, k * 5), f) })),
    [jour, h, f],
  );

  const versJour = (j: number): void => {
    const t = plusProche(aLHeure(j, h, m), instantsDuJour(j), f);
    if (t !== null) setV(t);
  };
  const versHeure = (x: number): void => {
    const t = plusProche(aLHeure(jour, x, m), Array.from({ length: 12 }, (_, k) => aLHeure(jour, x, k * 5)), f);
    if (t !== null) setV(t);
  };
  const versMinute = (x: number): void => {
    const t = aLHeure(jour, h, x);
    if (possible(t, f)) setV(t);
  };

  return (
    <Feuille label="Choisir le jour et l'heure" onFermer={props.onFermer}>
      <Calendrier jour={jour} maintenant={props.maintenant} fenetres={f} onJour={versJour} />
      <div className="hoa-heure-roues">
        <Roue label="Heure" valeurs={heures} valeur={h} onChange={versHeure} />
        <b aria-hidden="true">h</b>
        <Roue label="Minutes" valeurs={minutes} valeur={m} onChange={versMinute} />
      </div>
      <button type="button" className="hoa-go" onClick={() => props.onChoisir(v)}>
        {libelleQuand(v, props.maintenant)}
      </button>
    </Feuille>
  );
}

const JOURS_SEMAINE = ["L", "M", "M", "J", "V", "S", "D"];

function Calendrier(props: { jour: number; maintenant: number; fenetres: Fenetre[]; onJour: (j: number) => void }): JSX.Element {
  const premierMois = (t: number): number => {
    const d = new Date(t);
    return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  };
  const [mois, setMois] = useState(() => premierMois(props.jour));
  const d = new Date(mois);
  const annee = d.getFullYear();
  const m = d.getMonth();
  const decalage = (d.getDay() + 6) % 7; // lundi d'abord
  const nb = new Date(annee, m + 1, 0).getDate();
  const aujourdhui = debutJour(props.maintenant);
  const dernier = props.fenetres.reduce((x, [, b]) => Math.max(x, b), 0);
  const precedent = new Date(annee, m - 1, 1).getTime();
  const suivant = new Date(annee, m + 1, 1).getTime();
  const avantPossible = premierMois(props.maintenant) < mois;
  const apresPossible = suivant <= dernier;
  const jours = Array.from({ length: nb }, (_, i) => new Date(annee, m, i + 1).getTime());

  return (
    <div className="hoa-calendrier">
      <div className="tete">
        <button type="button" className="hoa-rond" aria-label="Mois précédent" disabled={!avantPossible} onClick={() => setMois(precedent)}>
          <Retour />
        </button>
        <b>{d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</b>
        <button type="button" className="hoa-rond suivant" aria-label="Mois suivant" disabled={!apresPossible} onClick={() => setMois(suivant)}>
          <Retour />
        </button>
      </div>
      <div className="grille" role="group" aria-label="Jour du rendez-vous">
        {JOURS_SEMAINE.map((j, i) => (
          <span key={`s${i}`} className="sem" aria-hidden="true">
            {j}
          </span>
        ))}
        {Array.from({ length: decalage }, (_, i) => (
          <span key={`v${i}`} aria-hidden="true" />
        ))}
        {jours.map((j) => {
          const ok = jourPossible(j, props.fenetres);
          return (
            <button
              key={j}
              type="button"
              className={j === aujourdhui ? "jour aujourdhui" : "jour"}
              aria-pressed={j === props.jour}
              aria-label={new Date(j).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
              disabled={!ok}
              onClick={() => props.onJour(j)}
            >
              {new Date(j).getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Une roue : une colonne qui défile et s'arrête sur une valeur, au centre.
 * Un appui choisit aussi ; les flèches du clavier passent à la voisine.
 * Une valeur grisée ne se choisit pas : la roue repart vers la plus proche.
 */
const RANG = 48;

function Roue(props: { label: string; valeurs: Array<{ v: number; texte: string; ok: boolean }>; valeur: number; onChange: (v: number) => void }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const attente = useRef<number | null>(null);
  const premier = useRef(true);
  const index = Math.max(0, props.valeurs.findIndex((x) => x.v === props.valeur));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const haut = index * RANG;
    if (Math.abs(el.scrollTop - haut) > 1) el.scrollTo({ top: haut, behavior: premier.current ? "auto" : "smooth" });
    premier.current = false;
  }, [index]);

  useEffect(
    () => () => {
      if (attente.current !== null) window.clearTimeout(attente.current);
    },
    [],
  );

  const voisine = (i: number, sens: 1 | -1 | 0): number => {
    if (sens === 0) {
      // La plus proche autorisée, de part et d'autre.
      for (let d = 0; d < props.valeurs.length; d++) {
        if (props.valeurs[i + d]?.ok) return i + d;
        if (props.valeurs[i - d]?.ok) return i - d;
      }
      return i;
    }
    for (let k = i + sens; k >= 0 && k < props.valeurs.length; k += sens) if (props.valeurs[k]!.ok) return k;
    return i;
  };

  const surDefilement = (): void => {
    if (attente.current !== null) window.clearTimeout(attente.current);
    attente.current = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const i = Math.min(props.valeurs.length - 1, Math.max(0, Math.round(el.scrollTop / RANG)));
      const j = voisine(i, 0);
      if (props.valeurs[j]!.v !== props.valeur) props.onChange(props.valeurs[j]!.v);
      else if (j !== i) el.scrollTo({ top: j * RANG, behavior: "smooth" });
    }, 120);
  };

  const touche = (e: ToucheReact<HTMLDivElement>): void => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const j = voisine(index, e.key === "ArrowDown" ? 1 : -1);
    if (j !== index) props.onChange(props.valeurs[j]!.v);
  };

  const courante = props.valeurs[index];
  return (
    <div
      ref={ref}
      className="hoa-roue"
      role="listbox"
      aria-label={props.label}
      aria-activedescendant={courante ? `roue-${props.label}-${courante.v}` : undefined}
      tabIndex={0}
      onScroll={surDefilement}
      onKeyDown={touche}
    >
      <div className="marge" aria-hidden="true" />
      {props.valeurs.map((x) => (
        <div
          key={x.v}
          id={`roue-${props.label}-${x.v}`}
          role="option"
          aria-selected={x.v === props.valeur}
          aria-disabled={!x.ok || undefined}
          className={`${x.v === props.valeur ? "rang choisi" : "rang"}${x.ok ? "" : " grise"}`}
          onClick={() => x.ok && props.onChange(x.v)}
        >
          {x.texte}
        </div>
      ))}
      <div className="marge" aria-hidden="true" />
    </div>
  );
}
